import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import MagicForm from '../Form/MagicForm';
import { ItemScheme } from '../Form/MagicFormItem';
import { MagicField } from '../../Enums/MagicField.enum';
import { FormValues } from '../../Modules/MagicForm/Interfaces/FormValues';

interface MedicalReportDetailLatamFormProps {
	onSubmit: (values: FormValues) => void;
	defaultData?: any;
	disableSubmit?: boolean;
}

const MedicalReportDetailLatamForm: React.FC<
	MedicalReportDetailLatamFormProps
> = ({ onSubmit, defaultData, disableSubmit }) => {
	const { translation } = useAppContext();
	const [formDefaultData, setFormDefaultData] = useState({
		addToPatientECard: true,
	});

	useEffect(() => {
		if (defaultData?.diagnosis?.length) {
			defaultData.diagnosis = defaultData.diagnosis.map(
				(dia: { code: string; type: string; codeLabel: string }) => {
					return { diagnosisItem: dia };
				},
			);
		}
		setFormDefaultData({
			...defaultData,
			addToPatientECard: true,
		});
	}, [defaultData]);

	// @ts-ignore
	const handleSubmit = async values => {
		if (values.consultationType?.value) {
			values.consultationType = values.consultationType?.value;
		}
		if (values.capillaryGlycemia) {
			values.capillaryGlycemiaUnit = 'mg/DL';
		}
		if (values.diagnosis.length) {
			values.diagnosis = values.diagnosis.map((dia: { diagnosisItem: any }) => {
				return { ...dia.diagnosisItem, termItemId: dia.diagnosisItem?.code };
			});
		}

		onSubmit(values);
	};

	const formScheme: ItemScheme[] = [
		{
			type: MagicField.SELECT,
			key: 'consultationType',
			title: 'component.medicalReportDetailLatamForm.consultationType',
			value: 'telemedicine',
			children: [
				{
					key: 'telemedicine',
					title: translation['base.meetingType.telemedicine'],
					value: 'telemedicine',
				},
				{
					key: 'onSite',
					title: translation['base.meetingType.onSite'],
					value: 'onSite',
				},
			],
			settings: {
				labelStyle: { fontWeight: '500', color: '#000', fontSize: '16px' },
				required: true,
			},
		},
		{
			type: MagicField.TEXTAREA,
			key: 'consultationReason',
			title: 'base.reasonOfConsultation',
			settings: {
				labelStyle: { fontWeight: '500', color: '#000', fontSize: '16px' },
				required: true,
			},
		},
		{
			type: MagicField.TEXTAREA,
			key: 'currentIllness',
			title: 'base.currentIllness',
			settings: {
				labelStyle: { fontWeight: '500', color: '#000', fontSize: '16px' },
				required: true,
			},
		},
		{
			type: MagicField.CONTAINER,
			key: 'personalHistory',
			children: [
				{
					type: MagicField.TEXT,
					title: 'base.personalHistory',
					subType: 'header',
				},
				{
					type: MagicField.TEXTAREA,
					key: 'medicaments',
					title:
						'component.medicalReportDetailLatamForm.personalHistory.medicaments',
				},
				{
					type: MagicField.TEXTAREA,
					key: 'surgeries',
					title:
						'component.medicalReportDetailLatamForm.personalHistory.surgeries',
				},
				{
					type: MagicField.TEXTAREA,
					key: 'medicamentAllergies',
					title:
						'component.medicalReportDetailLatamForm.personalHistory.medicamentAllergies',
				},
				{
					type: MagicField.TEXTAREA,
					key: 'additionalNote',
					title:
						'component.medicalReportDetailLatamForm.personalHistory.additionalNote',
				},
			],
		},
		{
			type: MagicField.CONTAINER,
			key: 'familyHistory',
			children: [
				{
					type: MagicField.TEXT,
					title: 'base.shared.familyHistory',
					subType: 'header',
				},
				{
					type: MagicField.TEXT,
					title:
						'component.medicalReportDetailLatamForm.familyHistory.familyIllnesses',
					subType: 'label',
					settings: {
						isOptional: true,
					},
				},
				{
					type: MagicField.CHECKBOX,
					key: 'diabetes.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.diabetes.toggle',
				},
				{
					type: MagicField.CHECKBOX,
					key: 'arterialHypertension.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.arterialHypertension.toggle',
				},
				{
					type: MagicField.CHECKBOX,
					key: 'dyslipidemia.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.dyslipidemia.toggle',
				},
				{
					type: MagicField.CONTAINER,
					key: 'hormonalDisorders',
					children: [
						{
							type: MagicField.CHECKBOX,
							key: 'toggle',
							title:
								'component.medicalReportDetailLatamForm.familyHistory.hormonalDisorders.toggle',
						},
						{
							type: MagicField.CONDITION,
							subType: 'view',
							value: 'familyHistory.hormonalDisorders.toggle',
							settings: {
								rules: [
									{
										formControlKey: 'familyHistory.hormonalDisorders.toggle',
										rule: 'equal',
										value: true,
									},
								],
							},
							children: [
								{
									type: MagicField.CONTAINER,
									settings: {
										showLine: true,
									},
									children: [
										{
											type: MagicField.TEXT_FIELD,
											key: 'specify',
											title: 'base.specify',
											settings: {
												required: true,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: MagicField.CHECKBOX,
					key: 'heartDisease.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.heartDisease.toggle',
				},
				{
					type: MagicField.CHECKBOX,
					key: 'ephropathy.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.ephropathy.toggle',
				},
				{
					type: MagicField.CHECKBOX,
					key: 'mentalDiseases.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.mentalDiseases.toggle',
				},
				{
					type: MagicField.CONTAINER,
					key: 'cancer',
					children: [
						{
							type: MagicField.CHECKBOX,
							key: 'toggle',
							title:
								'component.medicalReportDetailLatamForm.familyHistory.cancer.toggle',
						},
						{
							type: MagicField.CONDITION,
							subType: 'view',
							value: 'familyHistory.cancer.toggle',
							settings: {
								rules: [
									{
										formControlKey: 'familyHistory.cancer.toggle',
										rule: 'equal',
										value: true,
									},
								],
							},
							children: [
								{
									type: MagicField.CONTAINER,
									settings: {
										showLine: true,
									},
									children: [
										{
											type: MagicField.TEXT_FIELD,
											key: 'specify',
											title: 'base.specify',
											settings: {
												required: true,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: MagicField.CONTAINER,
					key: 'geneticDiseases',
					children: [
						{
							type: MagicField.CHECKBOX,
							key: 'toggle',
							title:
								'component.medicalReportDetailLatamForm.familyHistory.geneticDiseases.toggle',
						},
						{
							type: MagicField.CONDITION,
							subType: 'view',
							value: 'familyHistory.geneticDiseases.toggle',
							settings: {
								rules: [
									{
										formControlKey: 'familyHistory.geneticDiseases.toggle',
										rule: 'equal',
										value: true,
									},
								],
							},
							children: [
								{
									type: MagicField.CONTAINER,
									settings: {
										showLine: true,
									},
									children: [
										{
											type: MagicField.TEXT_FIELD,
											key: 'specify',
											title: 'base.specify',
											settings: {
												required: true,
											},
										},
									],
								},
							],
						},
					],
				},
				{
					type: MagicField.CHECKBOX,
					key: 'others.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.others.toggle',
				},
				{
					type: MagicField.CHECKBOX,
					key: 'niega.toggle',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.niega.toggle',
				},
				{
					type: MagicField.TEXT_FIELD,
					key: 'additionalNote',
					title:
						'component.medicalReportDetailLatamForm.familyHistory.additionalNote',
				},
			],
		},
		{
			type: MagicField.HR,
		},
		{
			type: MagicField.TEXT,
			title: 'component.medicalReportDetailLatamForm.psychologicalHabitsTitle',
			subType: 'header',
		},
		{
			type: MagicField.TEXTAREA,
			key: 'psychobiologicalHabits',
			title: 'component.medicalReportDetailLatamForm.psychologicalHabits',
		},
		{
			type: MagicField.TEXTAREA,
			key: 'functionalExam',
			title: 'component.medicalReportDetailLatamForm.functionalExam',
			settings: {
				labelStyle: { fontWeight: '500', color: '#000', fontSize: '16px' },
			},
		},
		{
			type: MagicField.TEXT,
			title: 'base.physicalExam',
			subType: 'header',
		},
		{
			type: MagicField.TEXT,
			title: 'component.medicalReportDetailLatamForm.vitalSigns',
			subType: 'paragraph',
		},
		{
			type: MagicField.BLOODPRESSURE,
			title: 'component.medicalReportDetailLatamForm.bloodPressure',
		},
		{
			type: MagicField.TEXT_FIELD,
			key: 'cardiacFrequency',
			title: 'component.medicalReportDetailLatamForm.cardiacFrequency',
			settings: {
				suffix: 'LPM',
			},
		},
		{
			type: MagicField.TEXT_FIELD,
			key: 'respiratoryFrequency',
			title: 'component.medicalReportDetailLatamForm.respiratoryFrequency',
			settings: {
				suffix: 'RPM',
			},
		},
		{
			type: MagicField.TEXT_FIELD,
			key: 'temperature',
			title: 'component.medicalReportDetailLatamForm.temperature',
			settings: {
				suffix: '°C',
			},
		},
		{
			type: MagicField.TEXT_FIELD,
			key: 'saturation',
			title: 'component.medicalReportDetailLatamForm.saturation',
			settings: {
				suffix: '%',
			},
		},
		{
			title: 'component.medicalReportDetailLatamForm.capillaryGlycemia',
			type: MagicField.GLYCAEMIA,
			key: 'capillaryGlycemia',
		},
		{
			type: MagicField.HSTACK,
			children: [
				{
					key: 'weight',
					type: MagicField.TEXT_FIELD,
					title: 'diabetes.entryForm.weight',
					settings: {
						suffix: 'base.shared.units.weight',
					},
				},
				{
					key: 'height',
					type: MagicField.TEXT_FIELD,
					title: 'diabetes.entryForm.height',
					settings: {
						suffix: 'base.shared.units.height',
					},
				},
				{
					type: MagicField.BMI,
					title: 'diabetes.entryForm.bmi',
					settings: {
						weightKey: 'weight',
						heightKey: 'height',
					},
				},
			],
		},
		{
			type: MagicField.TEXT_FIELD,
			key: 'abdominalTape',
			title: 'component.medicalReportDetailLatamForm.abdominalTape',
			settings: {
				suffix: 'cm',
			},
		},
		{
			type: MagicField.TEXTAREA,
			key: 'physicalExam',
			title: 'component.medicalReportDetailLatamForm.physicalExam',
		},
		{
			type: MagicField.TEXT,
			title: 'component.medicalReportDetailLatamForm.diagnosisTitle',
			subType: 'header',
		},
		{
			key: 'diagnosis',
			title: 'fields',
			type: MagicField.MULTIPLIER,
			children: [
				{
					type: MagicField.DIAGNOSIS_LATAM,
					key: 'diagnosisItem',
				},
				{
					type: MagicField.MULTIPLIER_REMOVE_BUTTON,
				},
			],
			settings: {
				createDefault: 1,
				addButtonTitle:
					'component.medicalReportDetailLatamForm.diagnosisAddButton',
			},
		},
		{
			type: MagicField.TEXT,
			title: 'component.medicalReportDetailLatamForm.caseAnalysis',
			subType: 'header',
		},
		{
			type: MagicField.TEXTAREA,
			key: 'medicalReport',
			title: 'component.medicalReportDetailLatamForm.medicalReport',
		},
		{
			type: MagicField.TEXTAREA,
			key: 'treatmentPlan',
			title: 'component.medicalReportDetailLatamForm.treatmentPlan',
		},
		{
			type: MagicField.WHITESPACE,
		},
		{
			type: MagicField.CHECKBOX,
			key: 'addToPatientECard',
			title: 'base.addToPatientECard',
		},
	];

	return (
		<MagicForm
			formScheme={formScheme}
			onSubmit={handleSubmit}
			defaultValues={formDefaultData}
			disableSubmit={disableSubmit}
		/>
	);
};

export default MedicalReportDetailLatamForm;
